import { keyframes, styled } from "../../../styles";

/**
 * Brands the native controls on an admin surface (EN-5146 UI feedback).
 * Checkboxes, radios, the date/time pickers' selected day and a range thumb
 * all paint themselves with the UA accent — Chrome's blue — which reads as
 * unstyled beside the portal's purple. `accent-color` is the only hook the
 * platform gives for the picker's popup, so it beats swapping these for
 * custom controls that could not open a calendar at all.
 *
 * Spread into `PageContainer` and `ModalContainer`: between them they wrap
 * every /admin surface, so no page has to remember.
 */
const brandedNativeControls = {
  "& input[type='checkbox'], & input[type='radio']": {
    width: "16px",
    height: "16px",
    accentColor: "$primary",
    cursor: "pointer",
  },

  "& input[type='date'], & input[type='datetime-local'], & input[type='time'], & input[type='month'], & input[type='week'], & input[type='range'], & progress":
    {
      accentColor: "$primary",
    },
};

/**
 * Page shell for an /admin route. `minHeight` rather than `height`: a list
 * page still fills the content area, so a `TableContainer` with `flex: 1`
 * pins its pagination to the bottom; but a detail page taller than the
 * viewport grows to its content instead of shrinking every child to fit,
 * which is what a definite height on a column flex container would do.
 */
export const PageContainer = styled("div", {
  display: "flex",
  flexDirection: "column",
  minHeight: "100%",
  padding: "24px",
  gap: "16px",

  ...brandedNativeControls,
});

/**
 * Breadcrumb + PageHeader as one block, so the trail sits tight above the
 * title instead of a full PageContainer gap away from it.
 */
export const PageIntro = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "6px",
});

export const PageHeader = styled("div", {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});

/**
 * Same face and colour as the portal's own page titles (`layout/PageTitle`:
 * Nunito Sans, $gray-100) so the two halves of the app read as one — EN-5146
 * UI feedback. The size stays larger than the portal's 18px: these titles
 * head a full page rather than a header bar.
 */
export const PageTitle = styled("h1", {
  fontSize: "24px",
  fontWeight: "600",
  color: "$gray-100",
  margin: 0,
  fontFamily: "$nunito",
});

/**
 * Page title and the info affordance that explains the page, kept together.
 * `PageHeader` is space-between, so an icon dropped straight in beside the
 * title would fly to the far right of the screen.
 */
export const PageTitleGroup = styled("div", {
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

/**
 * The "what is this page" trigger beside a page title (EN-5146 UI feedback) —
 * see `PageInfoTooltip`, which is the only thing that should render it.
 *
 * A real `<button>`, not a bare icon: the explainer it holds used to be a
 * standing block of copy, and a hover target that keyboard users cannot reach
 * would have taken it away from them entirely.
 */
export const InfoIconButton = styled("button", {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "22px",
  height: "22px",
  padding: 0,
  borderRadius: "50%",
  border: "none",
  backgroundColor: "transparent",
  color: "$gray-70",
  cursor: "pointer",
  transition: "background-color 0.15s, color 0.15s",

  "&:hover": {
    backgroundColor: "$primary-20",
    color: "$primary-80",
  },

  "&:focus-visible": {
    outline: "2px solid $primary-30",
    outlineOffset: "1px",
  },
});

/**
 * A bordered note in the page body. Since EN-5146 the standing page explainer
 * lives in `PageInfoTooltip` instead, so what is left here is the occasional
 * result message — "no rows in this environment" — which belongs in the flow
 * of the page rather than behind an icon.
 */
export const PageDescription = styled("p", {
  margin: 0,
  // Long enough for two lines of the longest disclaimer, short enough that
  // the eye finds the next line — the unstyled version ran ~1400px wide.
  maxWidth: "980px",
  padding: "12px 16px",
  borderRadius: "8px",
  border: "1px solid $gray-40",
  borderLeft: "3px solid $primary-30",
  backgroundColor: "$white",
  color: "$gray-100",
  fontFamily: "$openSans",
  fontSize: "13px",
  lineHeight: 1.6,
});

/**
 * Actions beside a section or panel title. Reserved for a card-level action
 * on a view with no filters row — a page-level create action belongs in
 * `FiltersActions` instead, so every list page puts it in the same place.
 */
export const HeaderActions = styled("div", {
  display: "flex",
  gap: "8px",
  alignItems: "center",
});

/**
 * The create action of a list view, pinned to the right-hand end of the
 * filters row (EN-5146 UI feedback).
 *
 * Every migrated page used to hang this off its `PageHeader`, level with the
 * title, and the result read as a different button on every page: the title
 * lengths differ, so the button landed at a different height and a different
 * distance from the content it acts on, and half the labels carried a "+"
 * glyph the other half didn't. Anchoring it to the filters row instead puts
 * it in one fixed spot — end of the search line, directly above the table —
 * and the wide search input pushes it there on its own.
 *
 * `marginLeft: auto` is what holds the right edge on the views whose filters
 * row has no flexible search input to do the pushing.
 */
export const FiltersActions = styled("div", {
  display: "flex",
  gap: "8px",
  alignItems: "center",
  marginLeft: "auto",

  // Match SearchInput rather than FilterButton's own 36px: this row's search
  // box is the thing the eye lines the button up against.
  "& > button": {
    height: "40px",
  },
});

export const FiltersContainer = styled("div", {
  display: "flex",
  flexWrap: "wrap",
  gap: "16px",
  alignItems: "flex-end",
  padding: "16px",
  backgroundColor: "$white",
  borderRadius: "8px",
  border: "1px solid $gray-30",
});

export const FilterGroup = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "6px",

  variants: {
    /**
     * Makes a field span every column of a `FormGrid`. For content whose
     * height doesn't match a normal field — a wrapping chip list, say — since
     * a grid row is as tall as its tallest cell and would otherwise push the
     * fields beside it far apart.
     */
    span: {
      full: {
        gridColumn: "1 / -1",
      },
    },
    /** Read-only fields: label sits 4px closer to its value. */
    compact: {
      true: {
        gap: "2px",
      },
    },
  },
});

export const FilterLabel = styled("label", {
  fontSize: "12px",
  fontWeight: "500",
  color: "$gray-80",
  fontFamily: "$openSans",
});

export const FilterInput = styled("input", {
  height: "36px",
  padding: "0 12px",
  borderRadius: "6px",
  border: "1px solid $gray-40",
  fontSize: "14px",
  fontFamily: "$openSans",
  color: "$charcoal",
  minWidth: "180px",
  // Grid tracks are already sized; without this a long value pushes the
  // column wider than its share.
  width: "100%",
  boxSizing: "border-box",

  "&:focus": {
    outline: "none",
    borderColor: "$primary",
  },

  "&::placeholder": {
    color: "$gray-60",
  },
});

/**
 * Full-width, label-less search input for admin list pages (EN-5129 UI
 * feedback): the placeholder alone says what it searches — no "Search"
 * label above it.
 */
export const SearchInput = styled("input", {
  flex: 1,
  // Wraps to its own line rather than collapsing when it shares the filters
  // row with a select and the action slot.
  minWidth: "260px",
  height: "40px",
  padding: "0 16px",
  borderRadius: "6px",
  border: "1px solid $gray-40",
  fontSize: "14px",
  fontFamily: "$openSans",
  color: "$charcoal",

  "&:focus": {
    outline: "none",
    borderColor: "$primary",
  },

  "&::placeholder": {
    color: "$gray-60",
  },
});

export const FilterSelect = styled("select", {
  height: "36px",
  padding: "0 12px",
  borderRadius: "6px",
  border: "1px solid $gray-40",
  fontSize: "14px",
  fontFamily: "$openSans",
  color: "$charcoal",
  minWidth: "180px",
  width: "100%",
  boxSizing: "border-box",
  backgroundColor: "$white",
  cursor: "pointer",

  "&:focus": {
    outline: "none",
    borderColor: "$primary",
  },
});

/** Makes the shared dropdown fill its form cell, as a native select did. */
export const SelectFieldWrapper = styled("div", {
  "& .ant-select": {
    width: "100% !important",
  },
});

/**
 * Makes the shared time picker fill its form cell. antd sizes it to its own
 * content, which left it visibly narrower than every field beside it — the
 * date picker takes a `width` prop for this, the time one does not.
 */
export const TimeFieldWrapper = styled("div", {
  "& .ant-picker": {
    width: "100%",
  },
});

export const FilterButton = styled("button", {
  height: "36px",
  // A floor, not a fixed width, so a row of actions reads as one set rather
  // than as buttons sized by how long each verb happens to be — "Save" next
  // to "Choose file" was nearly half the width (EN-5146 UI feedback). Longer
  // labels still grow past it.
  minWidth: "120px",
  padding: "0 16px",
  borderRadius: "6px",
  border: "none",
  fontSize: "14px",
  fontWeight: "500",
  fontFamily: "$openSans",
  cursor: "pointer",
  transition: "background-color 0.2s",

  variants: {
    variant: {
      primary: {
        backgroundColor: "$primary",
        color: "$white",
        "&:hover": {
          backgroundColor: "$primary-60",
        },
        "&:disabled": {
          backgroundColor: "$gray-40",
          cursor: "not-allowed",
        },
      },
      secondary: {
        backgroundColor: "$white",
        color: "$charcoal",
        border: "1px solid $gray-40",
        "&:hover": {
          backgroundColor: "$gray-20",
        },
        // Without this a disabled secondary button is indistinguishable from
        // an enabled one — it just silently ignores clicks.
        "&:disabled": {
          backgroundColor: "$gray-20",
          borderColor: "$gray-30",
          color: "$gray-60",
          cursor: "not-allowed",
        },
      },
    },
  },

  defaultVariants: {
    variant: "primary",
  },
});

export const DateRangeContainer = styled("div", {
  display: "flex",
  gap: "8px",
  alignItems: "center",
});

/**
 * The card an admin table sits in. Pages render `TableContainer` (the
 * component in `./TableContainer.tsx`), which draws this box and carries the
 * export context between the footer and the header.
 */
export const TableContainerBox = styled("div", {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  backgroundColor: "$white",
  borderRadius: "8px",
  border: "1px solid $gray-30",
  overflow: "hidden",
});

const spinAround = keyframes({
  "0%": { transform: "rotate(0deg)" },
  "100%": { transform: "rotate(360deg)" },
});

/**
 * An icon control that lives INSIDE the table's header row (Refresh, Export
 * CSV), so it borrows the header's design language: the header text colour,
 * a bare 16px glyph with no border or fill at rest, and the same soft hover
 * highlight the sort headers use.
 */
export const HeaderIconButton = styled("button", {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "22px",
  height: "22px",
  padding: 0,
  margin: 0,
  borderRadius: "4px",
  border: "none",
  backgroundColor: "transparent",
  color: "$gray-80",
  cursor: "pointer",
  flexShrink: 0,
  verticalAlign: "middle",
  transition: "background-color 0.15s, color 0.15s",

  "& svg": {
    width: "16px",
    height: "16px",
  },

  "&:hover:not(:disabled)": {
    backgroundColor: "#EDEDED",
    color: "$gray-100",
  },

  "&:focus-visible": {
    outline: "2px solid $primary-30",
    outlineOffset: "1px",
  },

  "&:disabled": {
    cursor: "not-allowed",
    color: "$gray-50",
  },
});

/** The Refresh control (see `RefreshButton`): a header icon that can spin. */
export const RefreshIconButton = styled(HeaderIconButton, {
  "&:disabled": {
    cursor: "progress",
    color: "$gray-60",
  },

  variants: {
    spinning: {
      true: {
        "& svg": {
          animation: `${spinAround} 0.9s linear infinite`,
        },
      },
    },
  },
});

/** Export and Refresh side by side at the end of the header row. */
export const HeaderIconGroup = styled("span", {
  display: "inline-flex",
  alignItems: "center",
  gap: "2px",
});

/**
 * The refresh-in-progress treatment: just a small caption pinned under the
 * header. The body's dimming is the `refreshing` variant of `TableScrollArea`;
 * nothing is veiled, so the rows stay legible.
 */
export const TableRefreshingOverlay = styled("div", {
  position: "absolute",
  top: "48px",
  left: 0,
  right: 0,
  display: "flex",
  justifyContent: "center",
  pointerEvents: "none",
  zIndex: 1,
});

export const TableRefreshingCaption = styled("span", {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  padding: "3px 10px",
  borderRadius: "12px",
  border: "1px solid $gray-30",
  backgroundColor: "$white",
  color: "$gray-80",
  fontSize: "12px",
  fontFamily: "$openSans",
});

/**
 * Scroll area for a `TableSorted` on an admin page (rendered by the shared
 * `TableWrapper` component, which also hands the table its header Refresh
 * control), and the one place the admin table's density is set (EN-5146 UI
 * feedback).
 *
 * `TableSorted` is the portal's clinician-facing table: 16px headers with
 * 40px of padding above them and 48px down the outer edges, sized for a
 * handful of wide columns on a patient list. An admin grid carries eight to
 * twelve, so that chrome eats the row before the data gets any and the header
 * alone stands taller than three rows of content.
 *
 * Scoped to this wrapper rather than added as a `TableSorted` variant so the
 * ~38 admin call sites pick it up without a prop threaded through every one,
 * and so nothing outside /admin shifts.
 */
export const TableScrollArea = styled("div", {
  flex: 1,
  overflow: "auto",
  // Anchors `TableRefreshingOverlay`.
  position: "relative",

  variants: {
    refreshing: {
      true: {
        "& table tbody": {
          opacity: 0.6,
        },
      },
    },
  },

  // The admin `TableContainer` already draws the border and the rounded
  // corners; TableSorted drawing its own inside them is a double line.
  "& > div": {
    border: "none",
    borderRadius: 0,
  },

  "& table thead": {
    borderBottom: "1px solid $gray-50",
  },

  "& table th": {
    fontSize: "13px",
    fontWeight: 600,
    color: "$gray-100",
    letterSpacing: "0.01em",
    backgroundColor: "$gray-30",
    padding: "10px 16px 10px 0",
    verticalAlign: "middle",
  },

  "& table th:first-child": {
    paddingLeft: "16px",
  },

  "& table th:last-child": {
    paddingRight: "16px",
  },

  "& table tbody tr": {
    fontSize: "14px",
  },

  // `:not([aria-label])` skips the virtualiser's spacer cells — they carry an
  // aria-label and an inline height, and vertical padding on them would open
  // a gap above and below every table body.
  "& table tbody td:not([aria-label])": {
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingRight: "16px",
  },

  "& table tbody td:first-child": {
    paddingLeft: "16px",
  },

  "& table tbody td:last-child": {
    paddingRight: "16px",
  },

  /**
   * With no rows, `TableSorted` sets its own height to `calc(100% - 68px)` —
   * a percentage resolved against whatever the wrapper happens to be, which
   * inside a modal is nothing. The message collapsed into a hairline directly
   * under the header. Size the table to its content instead, then give the
   * message a block of its own to sit in the middle of.
   */
  "& > div:has(> table + div)": {
    height: "auto",
    maxHeight: "none",
  },

  "& table + div": {
    minHeight: "150px",
    padding: "24px 16px",
    boxSizing: "border-box",

    "& p": {
      margin: 0,
      fontSize: "14px",
      color: "$gray-90",
      textAlign: "center",
    },
  },
});

export const TruncatedCell = styled("span", {
  display: "block",
  maxWidth: "300px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

export const PaginationContainer = styled("div", {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "12px 16px",
  borderTop: "1px solid $gray-30",
  backgroundColor: "$white",
});

export const PaginationInfo = styled("span", {
  display: "inline-flex",
  alignItems: "center",
  gap: "12px",
  fontSize: "14px",
  color: "$gray-80",
  fontFamily: "$openSans",
});

/**
 * Downloads what the table is currently showing (EN-5146). Sits beside the
 * row count rather than up in the page header, because the count is exactly
 * what it writes out — and because every admin table already renders this
 * footer, so one prop turns it on.
 */
export const ExportButton = styled("button", {
  height: "28px",
  padding: "0 12px",
  borderRadius: "4px",
  border: "1px solid $primary-30",
  backgroundColor: "$primary-20",
  color: "$primary-80",
  fontSize: "13px",
  fontFamily: "$openSans",
  cursor: "pointer",
  whiteSpace: "nowrap",
  transition: "background-color 0.15s, color 0.15s",

  "&:hover:not(:disabled)": {
    backgroundColor: "$primary",
    borderColor: "$primary",
    color: "$white",
  },

  "&:disabled": {
    borderColor: "$gray-40",
    backgroundColor: "$gray-20",
    color: "$gray-70",
    cursor: "not-allowed",
  },
});

export const PaginationControls = styled("div", {
  display: "flex",
  gap: "8px",
  alignItems: "center",
});

export const PaginationButton = styled("button", {
  height: "32px",
  padding: "0 12px",
  borderRadius: "4px",
  border: "1px solid $gray-40",
  backgroundColor: "$white",
  fontSize: "14px",
  fontFamily: "$openSans",
  color: "$charcoal",
  cursor: "pointer",
  transition: "all 0.2s",

  "&:hover:not(:disabled)": {
    backgroundColor: "$gray-20",
  },

  "&:disabled": {
    opacity: 0.5,
    cursor: "not-allowed",
  },
});

export const PageSizeSelect = styled("select", {
  height: "32px",
  padding: "0 8px",
  borderRadius: "4px",
  border: "1px solid $gray-40",
  backgroundColor: "$white",
  fontSize: "14px",
  fontFamily: "$openSans",
  color: "$charcoal",
  cursor: "pointer",

  "&:focus": {
    outline: "none",
    borderColor: "$primary",
  },
});

export const LoadingOverlay = styled("div", {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "48px",
  color: "$gray-80",
  fontFamily: "$openSans",
});

export const ErrorMessage = styled("div", {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "48px",
  color: "$error-light",
  fontFamily: "$openSans",
});

/**
 * Shell for a modal's content.
 *
 * The `gap` is the one thing that spaces a dialog's parts: it was a column
 * flex container with no gap, so every child sat flush against the next
 * unless it happened to carry a margin of its own. That is why an action
 * button landed hard on the table under it while the caption above it had
 * 24px of someone else's margin to sit in (EN-5146 UI feedback). Parts that
 * used to carry their own margins for this have had them removed — add rhythm
 * here, not on the pieces.
 */
export const ModalContainer = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  padding: "24px",
  maxHeight: "80vh",
  overflow: "auto",

  ...brandedNativeControls,
});

export const ModalTitle = styled("h2", {
  fontSize: "18px",
  fontWeight: "600",
  color: "$charcoal",
  margin: 0,
  fontFamily: "$openSans",
});

/**
 * Caption under a `ModalTitle` naming the record the dialog is about. Always
 * inside a {@link ModalHeader} — on its own it would be a full container gap
 * away from the title it belongs to.
 */
export const ModalSubtitle = styled("p", {
  margin: 0,
  fontSize: "14px",
  lineHeight: 1.5,
  color: "$gray-90",
  fontFamily: "$openSans",
});

/**
 * Title block of a modal: the title plus whatever names the record it is
 * about — a caption, a breadcrumb, or both. Groups them at one small gap and
 * steps the whole block away from the body once, instead of each part landing
 * wherever `ModalTitle`'s bottom margin happens to leave it.
 */
export const ModalHeader = styled("header", {
  display: "flex",
  flexDirection: "column",
  // Tighter than the container's gap: these parts are one block.
  gap: "6px",
});

export const ModalSection = styled("div", {});

/**
 * Vertical rhythm for modal forms (EN-5129 UI feedback): consistent 16px
 * gap between fields so inputs never sit flush against each other. Pair
 * with ModalFormActions for the button row.
 */
export const ModalForm = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "16px",
});

export const ModalFormActions = styled("div", {
  display: "flex",
  justifyContent: "flex-end",
  gap: "8px",
  marginTop: "8px",
});

export const ModalLabel = styled("span", {
  display: "block",
  fontSize: "12px",
  fontWeight: "500",
  color: "$gray-80",
  marginBottom: "4px",
  fontFamily: "$openSans",
});

export const ModalValue = styled("span", {
  display: "block",
  fontSize: "14px",
  color: "$charcoal",
  fontFamily: "$openSans",
  wordBreak: "break-word",
});

export const ModalJsonContainer = styled("pre", {
  backgroundColor: "$gray-20",
  padding: "12px",
  borderRadius: "6px",
  fontSize: "12px",
  fontFamily: "monospace",
  overflow: "auto",
  maxHeight: "200px",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  margin: 0,
});

export const ModalCloseButton = styled("button", {
  height: "40px",
  padding: "0 24px",
  borderRadius: "6px",
  border: "none",
  backgroundColor: "$primary",
  color: "$white",
  fontSize: "14px",
  fontWeight: "500",
  fontFamily: "$openSans",
  cursor: "pointer",
  alignSelf: "flex-end",

  "&:hover": {
    backgroundColor: "$primary-60",
  },
});

export const StatusBadge = styled("span", {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  fontSize: "13px",
  fontFamily: "$openSans",

  "&::before": {
    content: '""',
    display: "inline-block",
    width: "8px",
    height: "8px",
    borderRadius: "50%",
  },

  variants: {
    status: {
      active: {
        color: "$success-dark",
        "&::before": { backgroundColor: "$success" },
      },
      inactive: {
        color: "$gray-80",
        "&::before": { backgroundColor: "$gray-60" },
      },
      warning: {
        color: "$warning-dark",
        "&::before": { backgroundColor: "$warning" },
      },
      error: {
        color: "$error-light",
        "&::before": { backgroundColor: "$error-light" },
      },
    },
  },

  defaultVariants: {
    status: "active",
  },
});

export const BooleanCell = styled("span", {
  fontSize: "16px",

  variants: {
    value: {
      true: { color: "$success" },
      false: { color: "$error-light" },
    },
  },
});

export const TabsContainer = styled("div", {
  display: "flex",
  gap: "0",
  borderBottom: "2px solid $gray-30",
  marginBottom: "16px",
});

export const Tab = styled("button", {
  padding: "8px 20px",
  fontSize: "14px",
  fontWeight: "500",
  fontFamily: "$openSans",
  color: "$gray-80",
  backgroundColor: "transparent",
  border: "none",
  borderBottom: "2px solid transparent",
  marginBottom: "-2px",
  cursor: "pointer",
  transition: "all 0.2s",

  "&:hover": {
    color: "$charcoal",
  },

  variants: {
    active: {
      true: {
        color: "$primary",
        borderBottomColor: "$primary",
      },
    },
  },
});

export const StatsGrid = styled("div", {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
  gap: "16px",
  marginBottom: "16px",
});

export const StatCard = styled("div", {
  backgroundColor: "$white",
  borderRadius: "8px",
  border: "1px solid $gray-30",
  padding: "16px",
  display: "flex",
  flexDirection: "column",
  gap: "4px",
});

export const StatLabel = styled("span", {
  fontSize: "12px",
  fontWeight: "500",
  color: "$gray-80",
  fontFamily: "$openSans",
});

export const StatValue = styled("span", {
  fontSize: "24px",
  fontWeight: "600",
  color: "$charcoal",
  fontFamily: "$openSans",
});

/**
 * Card wrapper for a section of a detail page (EN-5150) — the migrated Retool
 * modules are mostly one or more forms, and each becomes a card.
 */
export const SectionCard = styled("section", {
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  padding: "20px 24px",
  backgroundColor: "$white",
  borderRadius: "8px",
  border: "1px solid $gray-30",
});

export const SectionHeader = styled("div", {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "16px",
});

/** Footer row of a SectionCard: a divider, then right-aligned actions. */
export const SectionActions = styled("div", {
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: "12px",
  paddingTop: "16px",
  borderTop: "1px solid $gray-30",
});

export const SectionTitle = styled("h2", {
  fontSize: "16px",
  fontWeight: "600",
  color: "$charcoal",
  margin: 0,
  fontFamily: "$openSans",
});

/**
 * Muted caption beside a SectionTitle — for identifiers that need to be
 * visible but don't deserve a field of their own (a record id used to sit in
 * the grid, costing a whole column to render one number).
 */
export const SectionCaption = styled("span", {
  fontSize: "12px",
  color: "$gray-60",
  fontFamily: "$openSans",
});

/**
 * Section title and the info icon that explains the section (EN-5146 UI
 * feedback). Section explainers used to be a `SectionCaption` beside the
 * title; like the page-level ones they now sit behind a `PageInfoTooltip`.
 * Centre-aligned, unlike `SectionHeading`, so the icon sits on the title's
 * midline rather than its baseline.
 */
export const SectionTitleGroup = styled("div", {
  display: "flex",
  alignItems: "center",
  gap: "6px",
});

/** Title + caption, so the caption tucks under/next to the title. */
export const SectionHeading = styled("div", {
  display: "flex",
  alignItems: "baseline",
  gap: "10px",
});

/**
 * Field grid for detail-page forms. A fixed column count, not `auto-fit`:
 * auto-fit stretched six fields into one row across the viewport with their
 * labels floating far apart. Wide screens get an extra column instead, so the
 * card can fill the content area while each field stays a readable ~300px
 * (EN-5150 review).
 *
 * Breakpoints are real widths, not the theme's `@md`/`@sm` aliases — those
 * are max-width 1680/768, so `@md` fires on every laptop.
 */
export const FormGrid = styled("div", {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: "20px 24px",

  "@media (max-width: 1400px)": {
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  },

  "@media (max-width: 1000px)": {
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  },

  "@media (max-width: 700px)": {
    gridTemplateColumns: "1fr",
  },
});

/**
 * A titled break inside a `FormGrid` (EN-5146 UI feedback).
 *
 * The hospital form runs its own fields and its feature switches through one
 * grid, and "Features" was introduced by a `FilterLabel` — the same element
 * that labels a text box — so the switches read as more of the record rather
 * than as a separate group. A rule and a heavier heading say where one ends.
 */
export const FormSubsection = styled("div", {
  gridColumn: "1 / -1",
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  marginTop: "4px",
  paddingTop: "20px",
  borderTop: "1px solid $gray-30",
});

export const FormSubsectionTitle = styled("h3", {
  margin: 0,
  fontFamily: "$openSans",
  fontSize: "14px",
  fontWeight: "600",
  color: "$charcoal",
});

/** Checkbox with its label on the right, aligned with the grid's fields. */
export const CheckboxField = styled("label", {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  // Matches the input height so a checkbox lines up with the fields beside it.
  minHeight: "36px",
  fontSize: "13px",
  fontFamily: "$openSans",
  color: "$charcoal",
  cursor: "pointer",

  "& input": {
    width: "16px",
    height: "16px",
    cursor: "pointer",
  },

  variants: {
    disabled: {
      true: {
        color: "$gray-60",
        cursor: "not-allowed",
        "& input": { cursor: "not-allowed" },
      },
    },
  },
});

/**
 * Validation message for a single field, rendered inside its `FilterGroup`
 * directly under the input. Distinct from `ErrorMessage`, which is a centred
 * 48px-padded panel for page-level failures — using that for field errors put
 * the text in the middle of the card, nowhere near the field it described.
 */
export const FieldError = styled("span", {
  fontSize: "12px",
  color: "$error-dark",
  fontFamily: "$openSans",
});

/**
 * Prose inside a modal — the sentence a confirmation is asking about.
 *
 * Eight page-local style files had defined this identically before EN-5169
 * needed a ninth. It lives here now; the copies get adopted when one of those
 * pages is next touched rather than in a sweep of its own (the TablePagination
 * rule). `line-height` and the `strong` weight are unified: two of the copies
 * had one and not the other.
 */
export const ModalBodyText = styled("p", {
  margin: 0,
  fontFamily: "$openSans",
  fontSize: "14px",
  lineHeight: 1.5,
  color: "$charcoal",

  "& strong": {
    fontWeight: 700,
  },
});

/**
 * Standing warning inside a confirmation modal — for an action that writes in
 * bulk or can't be undone. Deliberately not `FieldHint`: a grey footnote
 * under a destructive button reads as fine print, which is the opposite of
 * what it is (EN-5152 review).
 */
export const WarningNotice = styled("p", {
  margin: 0,
  padding: "10px 12px",
  borderRadius: "6px",
  backgroundColor: "$warning-lightest",
  color: "$warning-dark",
  border: "1px solid $warning",
  fontFamily: "$openSans",
  fontSize: "13px",
});

/** Muted hint under a field or an action, same slot as FieldError. */
export const FieldHint = styled("span", {
  fontSize: "12px",
  color: "$gray-60",
  fontFamily: "$openSans",
});

/** Non-editable value rendered where a field would sit. */
export const ReadOnlyValue = styled("span", {
  display: "inline-flex",
  alignItems: "center",
  // Matches FilterInput/FilterSelect so a read-only field lines up with the
  // editable ones on the same grid row.
  minHeight: "36px",
  fontSize: "14px",
  fontFamily: "$openSans",
  color: "$charcoal",
  wordBreak: "break-word",
});

/**
 * Embeddable panel chrome — a titled block a host page drops into its layout
 * (the shared `AuditLogPanel` and `PatientTreatmentsPanel`). Not a page
 * shell: it has no padding of its own and fills the width it is given.
 */
export const PanelContainer = styled("section", {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  width: "100%",
});

export const PanelHeader = styled("div", {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "16px",
});

export const PanelTitle = styled("h2", {
  fontSize: "18px",
  fontWeight: "600",
  color: "$charcoal",
  margin: 0,
  fontFamily: "$openSans",
});

/**
 * Mailto link in a table cell — Retool rendered email columns as links whose
 * clickCell event opened `mailto:{{ item }}`.
 */
export const EmailLink = styled("a", {
  color: "$primary",
  textDecoration: "none",
  fontFamily: "$openSans",

  "&:hover": {
    textDecoration: "underline",
  },
});

/**
 * Row-level action button for a table cell (EN-5165).
 *
 * Every migrated page defines its own copy of this in its `.styles.ts`; the
 * shared one exists because `AlertsPanel` is embedded by other pages and
 * cannot reach into a page's local styles. Worth collapsing the page-local
 * copies onto this later — they are byte-identical.
 */
/**
 * A row action, as an icon (EN-5146 UI feedback) — see `RowAction`, which is
 * the only thing that should render it and which supplies the tooltip and the
 * accessible name a glyph on its own cannot carry.
 *
 * Square rather than the old text pill: the labels ran from "Open" to "Sync
 * tenant IDs", so an actions column was as wide as its wordiest row and every
 * table lost that width from its data. Fixed-width icons cost the same on
 * every page.
 */
export const RowActionIcon = styled("button", {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "28px",
  height: "28px",
  padding: 0,
  borderRadius: "4px",
  border: "1px solid $gray-40",
  backgroundColor: "$white",
  color: "$gray-100",
  cursor: "pointer",
  transition: "background-color 0.15s, color 0.15s, border-color 0.15s",

  "&:hover:not(:disabled)": {
    backgroundColor: "$primary-20",
    borderColor: "$primary-30",
    color: "$primary-80",
  },

  "&:focus-visible": {
    outline: "2px solid $primary-30",
    outlineOffset: "1px",
  },

  "&:disabled": {
    opacity: 0.5,
    cursor: "not-allowed",
  },

  variants: {
    /**
     * For an action that removes, disconnects or deactivates. Only the hover
     * turns red — a row of permanently red buttons reads as an error state.
     */
    tone: {
      danger: {
        "&:hover:not(:disabled)": {
          backgroundColor: "$error-lightest",
          borderColor: "$error-light",
          color: "$error-dark",
        },
      },
    },
  },
});

/** Right-aligned container for one or more {@link RowActionIcon}. */
export const RowActionGroup = styled("div", {
  display: "flex",
  gap: "6px",
  justifyContent: "flex-end",
});

const spin = keyframes({
  to: { transform: "rotate(360deg)" },
});

/**
 * Body of a table's loading or empty state — see `TableLoadingState`.
 * A row of its own rather than bare text so a spinner can sit beside it.
 */
export const TableStateMessage = styled("span", {
  display: "inline-flex",
  alignItems: "center",
  gap: "10px",
  fontSize: "14px",
  color: "$gray-90",
  fontFamily: "$openSans",
});

export const TableStateSpinner = styled("span", {
  width: "16px",
  height: "16px",
  flexShrink: 0,
  borderRadius: "50%",
  border: "2px solid $gray-50",
  borderTopColor: "$primary",
  animation: `${spin} 700ms linear infinite`,
});
